import {
  Card,
  CardContent,
  Chip,
  Collapse,
  createStyles,
  Grid,
  Switch,
  Typography,
  withStyles,
  WithStyles
} from "@material-ui/core";
import {
  faChevronDown,
  faLock,
  faLockOpen
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { get } from "lodash";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import colors from "../../../../../theme/colors";
import {
  getFreezeMeta,
  getIsFrozen
} from "../../../../../modules/factoringClient";
import FreezeAuditTrail from "./FreezeAuditTrail";
import FreezeExemptContractTypesForm from "./FreezeExemptContractTypesForm";

const styles = () =>
  createStyles({
    card: {
      marginTop: 0
    },
    headerRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: 16,
      "& > *:not(:last-child)": {
        marginRight: 10
      }
    },
    title: {
      fontWeight: 600
    },
    titleFrozen: {
      fontWeight: 600,
      color: colors.orange
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: 8,
      "& > *:not(:last-child)": {
        marginRight: 10
      }
    },
    chip: {
      color: colors.white,
      backgroundColor: colors.orange,
      fontWeight: 600,
      letterSpacing: 0.5
    },
    warning: {
      color: colors.danger,
      fontStyle: "italic",
      marginBottom: 8
    },
    detailGrid: {
      marginTop: 12,
      marginBottom: 8
    },
    detailLabel: {
      color: colors.grayText,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 0.5
    },
    detailValue: {
      color: colors.textColor,
      wordBreak: "break-word"
    },
    auditHeaderRow: {
      display: "flex",
      alignItems: "center",
      marginTop: 20,
      cursor: "pointer",
      userSelect: "none"
    },
    auditHeader: {
      fontWeight: 600
    },
    auditHint: {
      color: colors.grayText,
      fontStyle: "italic",
      fontSize: 12
    },
    chevron: {
      marginLeft: 3,
      marginRight: 10,
      color: colors.grayText,
      transition: "transform 0.2s"
    }
  });

const formatTimestamp = (iso?: string | null): string => {
  if (!iso) {
    return "—";
  }
  try {
    const d = new Date(iso);
    return `${d.toISOString().slice(0, 19).replace("T", " ")} UTC`;
  } catch (e) {
    return iso;
  }
};

interface Props extends WithStyles<typeof styles> {
  factoringId: string;
}

const ReceivablesFreezePanel = ({ classes, factoringId }: Props): JSX.Element => {
  const isFrozen = useSelector(getIsFrozen);
  const meta = useSelector(getFreezeMeta);
  const [auditExpanded, setAuditExpanded] = useState<boolean>(false);

  const frozenBy = get(meta, "by.email") || "—";
  const frozenAt = formatTimestamp(get(meta, "at"));
  const frozenReason = get(meta, "reason") || "—";
  const frozenScope = get(meta, "scope") || "—";

  const toggleAuditExpanded = (): void => setAuditExpanded(prev => !prev);

  return (
    <Card className={classes.card}>
      <CardContent>
        <div className={classes.headerRow}>
          <FontAwesomeIcon
            icon={isFrozen ? faLock : faLockOpen}
            color={isFrozen ? colors.orange : colors.grayText}
          />
          <Typography
            variant="h6"
            className={isFrozen ? classes.titleFrozen : classes.title}
          >
            {isFrozen ? "Receivables Freeze — Active" : "Receivables Freeze"}
          </Typography>
        </div>

        <div className={classes.statusRow}>
          {isFrozen ? (
            <Chip
              label="FROZEN"
              size="small"
              className={classes.chip}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No active freeze — all operations permitted
            </Typography>
          )}
        </div>

        <FreezeExemptContractTypesForm factoringId={factoringId} />

        {isFrozen && (
          <>
            <Typography variant="body2" className={classes.warning}>
              All balance-mutating operations are blocked for this client.
            </Typography>
            <Grid container spacing={16} className={classes.detailGrid}>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.detailLabel}>
                  Frozen By
                </Typography>
                <Typography className={classes.detailValue}>
                  {frozenBy}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.detailLabel}>
                  Frozen At
                </Typography>
                <Typography className={classes.detailValue}>
                  {frozenAt}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.detailLabel}>
                  Reason / Notes
                </Typography>
                <Typography className={classes.detailValue}>
                  {frozenReason}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.detailLabel}>
                  Freeze Scope
                </Typography>
                <Typography className={classes.detailValue}>
                  {frozenScope}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}

        <div
          className={classes.auditHeaderRow}
          onClick={toggleAuditExpanded}
          role="button"
          tabIndex={0}
          onKeyDown={(e): void => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleAuditExpanded();
            }
          }}
        >
          <Typography variant="subtitle1" className={classes.auditHeader}>
            Freeze Audit Trail
          </Typography>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={classes.chevron}
            style={{
              transform: auditExpanded ? "rotate(180deg)" : "rotate(0deg)"
            }}
          />
          {!auditExpanded && (
            <Typography variant="caption" className={classes.auditHint}>
              Click here to see details
            </Typography>
          )}
        </div>
        <Collapse in={auditExpanded}>
          <FreezeAuditTrail factoringId={factoringId} />
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default withStyles(styles)(ReceivablesFreezePanel);
